<template>
  <button :disabled="disabled" class="checkout-header-control" @click="onClick">
    <svg width="24" height="24" viewBox="0 0 24 24" :fill="color">
      <path :d="icon" />
    </svg>
  </button>
</template>
<script setup lang="ts">
import { computed } from 'vue'
import { mdiChevronDown, mdiChevronUp } from '@mdi/js'
import { useAmplitude } from '@outputinc/o-analytics'
import { useScreenStore } from '@/store/ScreenStore'
import { storeToRefs } from 'pinia'
import { useCheckoutStore } from '@/store/CheckoutStore'

const screenStore = useScreenStore()
const checkoutStore = useCheckoutStore()
const { track, Events } = useAmplitude()
const { nextScreen, previousScreen, currentScreenProduct, canNavigateNext } = storeToRefs(screenStore)
const { isComplete } = storeToRefs(checkoutStore)
const { navigateNext, navigatePrevious } = screenStore

const props = defineProps({
  previous: {
    type: Boolean,
    default: false
  },
  next: {
    type: Boolean,
    default: false
  }
})

const disabled = computed(() => {
  if (props.next) {
    return isComplete.value || !screenToNavigateTo.value || !canNavigateNext.value
  }
  return isComplete.value || !screenToNavigateTo.value
})
const icon = computed(() => (props.previous ? mdiChevronUp : mdiChevronDown))
const screenToNavigateTo = computed(() => (props.next ? nextScreen.value : previousScreen?.value))

const color = computed(() => {
  // Redesign theme colors
  return screenToNavigateTo.value && !disabled.value
    ? '#0d0e0f' // Black (gray5)
    : '#918b7d' // Tan inactive
})

const onClick = () => {
  const eventName = Events.CHECKOUT_SUBSCRIPTION_UI_NAVIGATE
  const eventProperties = { source: 'header', products: [currentScreenProduct.value] }
  if (props.next) {
    track(eventName, { ...eventProperties, direction: 'next' })
    navigateNext()
    return true
  }
  if (props.previous) {
    track(eventName, { ...eventProperties, direction: 'previous' })
    navigatePrevious()
    return true
  }
}
</script>
<style lang="scss" scoped>
@import '@/assets/styles/inc/variables';

.checkout-header-control {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: $spacer-2;
  background: transparent;
  border: solid 2px var(--color-border-secondary);
  border-radius: $btn-border-radius;
  cursor: pointer;
  transition: $transition-base;
  min-width: 44px;
  min-height: 44px;

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:disabled {
    background: transparent;
    border-color: var(--color-border-primary);
    cursor: not-allowed;
  }

  svg {
    display: block;
  }
}
</style>
